import supabase from '../config/supabase';
import config from '../config/environment';
import {
  EQUIPMENT_CATEGORY_ORDER,
  isEquipmentCategory,
  type EquipmentCategory,
  type EquipmentItem,
} from '../constants/equipmentCategory';
import { PublicStudentTokenAlreadyUsedError } from './PublicStudentContextService';
import { handleServiceError } from '../utils/errorHandler';

interface EquipmentApiRow {
  id?: number;
  name?: string;
  category?: string;
  is_active?: boolean;
  isActive?: boolean;
  image_url?: string | null;
  imageUrl?: string | null;
}

const throwPublicTokenError = (errorMessage: string, code?: string): never => {
  if (code === 'TOKEN_ALREADY_USED' || /já recebemos/i.test(errorMessage)) {
    throw new PublicStudentTokenAlreadyUsedError(errorMessage);
  }

  throw new Error(errorMessage);
};

class StudentEquipmentServiceClass {
  private async getAuthHeaders(studentToken?: string): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      apikey: config.supabaseAnonKey,
    };

    if (studentToken) {
      return headers;
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    return headers;
  }

  private mapEquipmentRow(row: EquipmentApiRow): EquipmentItem | null {
    const id = Number(row.id);
    const name = typeof row.name === 'string' ? row.name.trim() : '';
    const category = typeof row.category === 'string' ? row.category : '';

    if (!Number.isInteger(id) || id <= 0 || !name || !isEquipmentCategory(category)) {
      return null;
    }

    const imageUrlRaw = typeof row.image_url === 'string'
      ? row.image_url
      : (typeof row.imageUrl === 'string' ? row.imageUrl : null);

    return {
      id,
      name,
      category,
      isActive: row.is_active === true || row.isActive === true,
      imageUrl: imageUrlRaw?.trim() || null,
    };
  }

  async listCatalog(params: {
    studentId: number;
    studentToken?: string;
  }): Promise<EquipmentItem[]> {
    if (!config.supabaseFunctionsUrl) {
      handleServiceError(new Error('URL das Edge Functions não configurada.'), 'listar equipamentos');
    }

    const headers = await this.getAuthHeaders(params.studentToken);
    const response = await fetch(`${config.supabaseFunctionsUrl}/upload-student-photo`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'list-equipment-catalog',
        studentId: params.studentId,
        studentToken: params.studentToken,
      }),
    });

    const body = await response.json().catch(() => ({})) as {
      success?: boolean;
      error?: string;
      code?: string;
      data?: { equipments?: EquipmentApiRow[] };
    };

    if (!response.ok || body.success === false) {
      throwPublicTokenError(
        body.error || 'Não foi possível carregar os equipamentos.',
        body.code,
      );
    }

    return (body.data?.equipments ?? [])
      .map((row) => this.mapEquipmentRow(row))
      .filter((item): item is EquipmentItem => item !== null);
  }

  async getSelectedIds(params: {
    studentId: number;
    studentToken?: string;
  }): Promise<number[]> {
    if (!config.supabaseFunctionsUrl) {
      handleServiceError(new Error('URL das Edge Functions não configurada.'), 'carregar equipamentos da aluna');
    }

    const headers = await this.getAuthHeaders(params.studentToken);
    const response = await fetch(`${config.supabaseFunctionsUrl}/upload-student-photo`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'get-student-equipments',
        studentId: params.studentId,
        studentToken: params.studentToken,
      }),
    });

    const body = await response.json().catch(() => ({})) as {
      success?: boolean;
      error?: string;
      code?: string;
      data?: { equipmentIds?: number[] };
    };

    if (!response.ok || body.success === false) {
      throwPublicTokenError(
        body.error || 'Não foi possível carregar os equipamentos da aluna.',
        body.code,
      );
    }

    return (body.data?.equipmentIds ?? [])
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);
  }

  async syncSelection(params: {
    studentId: number;
    equipmentIds: number[];
    studentToken?: string;
  }): Promise<number[]> {
    if (!config.supabaseFunctionsUrl) {
      handleServiceError(new Error('URL das Edge Functions não configurada.'), 'salvar equipamentos');
    }

    const headers = await this.getAuthHeaders(params.studentToken);
    const response = await fetch(`${config.supabaseFunctionsUrl}/upload-student-photo`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'sync-student-equipments',
        studentId: params.studentId,
        equipmentIds: params.equipmentIds,
        studentToken: params.studentToken,
      }),
    });

    const body = await response.json().catch(() => ({})) as {
      success?: boolean;
      error?: string;
      code?: string;
      data?: { equipmentIds?: number[] };
    };

    if (!response.ok || body.success === false) {
      throwPublicTokenError(
        body.error || 'Não foi possível salvar os equipamentos.',
        body.code,
      );
    }

    return (body.data?.equipmentIds ?? [])
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);
  }

  /** Portal: load via RLS (authenticated). */
  async listSelectedForStudent(studentId: number): Promise<EquipmentItem[]> {
    const [catalog, selectedIds] = await Promise.all([
      this.listCatalog({ studentId }),
      this.getSelectedIds({ studentId }),
    ]);

    const selectedIdSet = new Set(selectedIds);
    const items = catalog.filter((item) => selectedIdSet.has(item.id));

    return items.sort((left, right) => {
      const categoryDiff = EQUIPMENT_CATEGORY_ORDER.indexOf(left.category)
        - EQUIPMENT_CATEGORY_ORDER.indexOf(right.category);
      if (categoryDiff !== 0) {
        return categoryDiff;
      }
      return left.name.localeCompare(right.name, 'pt-BR');
    });
  }

  groupByCategory(items: EquipmentItem[]): Array<{
    category: EquipmentCategory;
    items: EquipmentItem[];
  }> {
    return EQUIPMENT_CATEGORY_ORDER
      .map((category) => ({
        category,
        items: items.filter((item) => item.category === category),
      }))
      .filter((group) => group.items.length > 0);
  }
}

export const StudentEquipmentService = new StudentEquipmentServiceClass();
